import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import '@polymer/polymer/lib/elements/dom-if.js';
import {PHONE_CONFIG} from './phone-config.js';
import {PHONE_TRANSLATIONS} from './phone-translations.js';
import {AREA_CODE_LIST} from './phone-area-code-list.js';
import template from './ns-phone.html';
import {FormMixin} from '../formMixin.js';
import '../ns-form/ns-form.js';

/**
 * Used when an input for a phone is needed.
 * This component takes the input and validates it against the provided rules.
 * Also has the ability to use a mask to properly format user input.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Phone Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-phone></ns-phone>
 *   </template>
 * </demo-snippet>
 */
export class NsPhone extends FormMixin(PolymerElement) {
    static get is() {
        return 'ns-phone';
    }

    /**
     * ns-phone html template
     */
    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    /**
     * Returns properties of ns-phone
     */
    static get properties() {
        return {
            /**
             * List of phone prefixes and associated country codes
             */
            areaCodeList: {
                type: Array,
                value: () => AREA_CODE_LIST
            },
            /**
             * Country code of phone prefix
             */
            areaCode: {
                type: String,
                notify: true,
                observer: '_onAreaCodeChanged'
            },
            /**
             * Index of selected phone prefix in dropdown
             */
            areaCodeIdx: {
                type: Number,
                observer: '_onAreaCodeIdxChanged'
            },
            /**
             * country
             * Used to indicate country for localization
             */
            country: {
                type: String,
                value: 'US',
                observer: '_initialize'
            },
            /**
             * language
             * Used to indicate language for localization
             */
            language: {
                type: String,
                value: 'en',
                observer: '_initialize'
            },
            /**
             * required
             * Used to indicate the phone input is required to proceed
             */
            required: {
                type: Boolean,
                value: false,
                reflectToAttribute: true
            },
            /**
             * value
             * Passed in as value to ns-input.  Two-way binding utilized.
             */
            value: {
                type: String,
                notify: true
            },
            /**
             * Used to identify if entry is valid
             */
            valid: {
                type: Boolean,
                notify: true,
                observer: '_onValidChanged'
            },
            /**
             * Property populates a label for the input.
             * By default this is empty, and does not display anything.
             */
            label: {
                type: String,
                reflectToAttribute: true
            },
            /**
             * Used to display when field doesnt match phone number pattern
             */
            invalidMessage: {
                type: String,
                value: 'Invalid value'
            },
            /**
             * Property is used as a flag to track if input element is in focus.
             */
            focused: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Added to the field label to indicate that field is optional (default: '')
             */
            optional: {
                type: String,
                value: ''
            },
            /**
             * dirty
             * Dirty flag for ns-phone
             * Will be set to true when phone value changes.
             */
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },
            useServerTranslations: {
                type: Boolean,
                value: false
            },
            serverPhoneLabel: {
                type: String
            },
            fieldType: {
                type: String
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(valueDirty)'
        ];
    }

    /**
     * When component is initialized it will pull the translations
     * translations are pulled based on country and language
     * rules for the inputs validation are also pulled in based on country
     */
    connectedCallback() {
        super.connectedCallback();
        this._initialize();
    }

    _initialize() {
        if (this.country && this.country.length === 2 && this.language && this.language.length === 2) {
            let serverTranslations = {
                label: this.serverPhoneLabel
            }
            this._localize(this.language, this.country, PHONE_TRANSLATIONS, serverTranslations, this.useServerTranslations);
            this._setConfig(this.country, PHONE_CONFIG);
            
            // This was first removed for all but japan in: CX15-3449, added in France for validation
            if (this.country !== 'JP' && this.country !== 'FR') {
                this.regex = '';
            }

            if (this.showAreaCodeDropdown) {
                this.areaCode = this.areaCode || this.country;
            }
        }
    }

    /**
     * Form validation - ns-form looks for this function for validation
     */
    validate() {
        return this.shadowRoot.querySelector('ns-form').validate();
    }

    focus() {
        this.focused = true;
    }

    blur() {
        this.focused = false;
        this.validate();
    }

    _onAreaCodeIdxChanged(areaCodeIdx) {
        if (areaCodeIdx > -1) {
            const code = this.areaCodeList[areaCodeIdx].key;
            if (code !== this.areaCode) this.areaCode = code;
        }
    }
    
    _onAreaCodeChanged(code) {
        this.mask = PHONE_CONFIG[this.areaCode] 
            ? PHONE_CONFIG[this.areaCode].mask 
            : this.mask;
        //turning off setting regex for changing area codes for SUB-1188
        // this.regex = PHONE_CONFIG[this.areaCode] 
        //     ? PHONE_CONFIG[this.areaCode].regex 
        //     : this.regex;
        
        const idx = this.areaCodeList.findIndex(el => el.key === code);
        if (idx > -1 && idx !== this.areaCodeIdx) this.areaCodeIdx = idx;
    }

    _onValidChanged(valid) {
        if (valid) {
            this.labelClass = '';
        } else {
            this.labelClass = 'error';
        }
    }
}

customElements.define(NsPhone.is, NsPhone);
